import type { ReactNode } from "react";
import { VisuallyHidden } from "../a11y/VisuallyHidden";
import { Text } from "./Text";
import styles from "./InertControl.module.css";

/**
 * The spoken form of an unavailable control (§8.6). The comma matters: it is
 * what makes the state read as a separate clause rather than as part of the
 * control's name.
 */
export function unavailableLabel(title: string): string {
  return `${title}, unavailable`;
}

/**
 * The visible half of §8.6's clause, for the row form.
 *
 * It is the spec's own word rather than a friendlier synonym, because the
 * sighted user and the screen-reader user should be able to describe the same
 * row to each other and recognize it. It is also the §8.4 redundancy carrier
 * for this state: without it the row is distinguished only by being a little
 * quieter than its neighbours, which is a hue-and-value difference and nothing
 * a user can name.
 */
export const UNAVAILABLE_STATE_WORD = "Unavailable" as const;

/**
 * The complete announcement for an unavailable control: §8.6's clause, then —
 * when the caller supplied one — the reason, as its own sentence.
 *
 * The reason is appended to the single label rather than exposed as a separate
 * node because §8.6 makes the label the *whole* announcement; a second focusable
 * or separately-navigable node would put the explanation somewhere the user has
 * to go looking for it, which is the failure the explanation exists to prevent.
 */
export function unavailableAnnouncement(title: string, reason?: string): string {
  const label = unavailableLabel(title);
  if (reason === undefined || reason === "") return label;
  return `${label}. ${reason}`;
}

/**
 * The hint on a control that is enabled but locked. It is deliberately
 * different from the unavailable label: locked means "there is a way to do
 * this", and the hint says what it is.
 */
export const LOCKED_CONTROL_HINT = "Unlock controls to use this action" as const;

/**
 * `inline` replaces a control inside a dense composition — a transport-deck
 * button, a menu item, a chip. `row` replaces a control that *was* a full row
 * in a list of rows, and keeps that row's geometry so the list does not appear
 * to lose an entry.
 */
export type InertControlLayout = "inline" | "row";

export interface InertControlProps {
  /** The control's ordinary title. The ", unavailable" clause is added here. */
  readonly title: string;
  /**
   * The visual content of the control it replaces — a glyph, a label, a value.
   * It is hidden from assistive technology, matching the native "children
   * ignored": the single spoken label is the whole announcement.
   *
   * Ignored by the `row` layout, which composes its own visible content from
   * `title`, `detail` and `reason`.
   */
  readonly children?: ReactNode;
  /**
   * `row` only: the supporting line the available row would have carried. An
   * unavailable row that drops it tells the user less about the feature than
   * the working row did, which inverts what they needed.
   */
  readonly detail?: string;
  /**
   * `row` only: why this platform cannot offer the control. "Unavailable" on
   * its own is indistinguishable from a bug; a reason is what turns it into a
   * decision the user can accept and stop looking for.
   */
  readonly reason?: string;
  readonly layout?: InertControlLayout;
  readonly fullWidth?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

/**
 * A control that is unavailable.
 *
 * This is **not** a disabled button, and the difference is structural rather
 * than styling (§8.6). A `<button disabled>` is still an element with the
 * button role: assistive technology finds it, offers it in the controls rotor,
 * and lets the user activate it — at which point nothing happens and the user
 * has no way to tell whether the app is broken or the action is simply not
 * available yet. Rendering a plain labelled element removes it from the set of
 * things that can be activated at all, and the label says why it is there.
 *
 * `aria-disabled` is not used for the same reason: it keeps the button role.
 *
 * The bounds of a `NumberStepper` are the deliberate exception — those *are*
 * disabled buttons, because a stepper at its bound is a control the user is
 * actively using and hiding half of it mid-edit would lose their place.
 */
export function InertControl({
  title,
  children,
  detail,
  reason,
  layout = "inline",
  fullWidth = false,
  className,
  testId,
}: InertControlProps) {
  const classes = [
    layout === "row" ? styles.row : styles.inert,
    layout === "inline" && fullWidth ? styles.full : undefined,
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  if (layout === "row") {
    return (
      <span
        className={classes}
        data-testid={testId}
        data-ff-inert="true"
        data-ff-inert-layout="row"
      >
        {/* Visually the row is title over detail over reason, in the same
            column position as a working row's labels. All of it is hidden from
            assistive technology so the one spoken label below stays the whole
            announcement (§8.6) — it already contains the title and the reason. */}
        <span className={styles.rowLabels} aria-hidden="true">
          <Text variant="body" tone="secondary">
            {title}
          </Text>
          {detail !== undefined && detail !== "" && (
            <Text variant="caption" tone="tertiary">
              {detail}
            </Text>
          )}
          {reason !== undefined && reason !== "" && (
            <Text variant="caption" tone="tertiary">
              {reason}
            </Text>
          )}
        </span>
        {/* Occupies the slot the switch or stepper would have occupied, so the
            list keeps one alignment down its trailing edge. */}
        <span className={styles.rowState} aria-hidden="true">
          <Text variant="caption2" tone="tertiary" weight="medium" uppercase>
            {UNAVAILABLE_STATE_WORD}
          </Text>
        </span>
        <VisuallyHidden>{unavailableAnnouncement(title, reason)}</VisuallyHidden>
      </span>
    );
  }

  return (
    <span className={classes} data-testid={testId} data-ff-inert="true">
      {children !== undefined && <span aria-hidden="true">{children}</span>}
      <VisuallyHidden>{unavailableAnnouncement(title, reason)}</VisuallyHidden>
    </span>
  );
}
